import React, { useEffect, useMemo, useState } from "react";

import { IconButton, useMediaQuery } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import clsx from "clsx";
import { useLocation, useNavigate } from "react-router";

import SwipeableViews from "react-swipeable-views";
import { mod } from "react-swipeable-views-core";
import { virtualize } from "react-swipeable-views-utils";

import CartModal from "components/CartModal";
import { CheckoutPanel, CustomerInfoPanel } from "components/Checkout";
import { DesktopCheckoutPanel } from "components/CheckoutDesktop";
import RouteDrawer, { RouteDrawerProps } from "components/RouteDrawer";
import DesktopContainerDrawer from "containers/DesktopContainerDrawer";
import MobileContainerDrawer from "containers/MobileContainerDrawer";

import makeStyles from "hooks/makeStyles";
import useCart from "hooks/useCart";
import useLocale from "hooks/useLocale";

const VirtualizeSwipeableViews = virtualize(SwipeableViews);

const useStyles = makeStyles(theme => ({
	root: {},
	closeButton: {
		marginLeft: -2,
		marginTop: -2,
		padding: 0,
		textAlign: "left",
		alignSelf: "flex-start"
	},
	closeIcon: {
		width: 16,
		height: 16,
		color: "#fff",
		[theme.breakpoints.up(480)]: {
			display: "none"
		}
	},
	paper: {
		borderRadius: `${theme.spacing(2)}px ${theme.spacing(2)}px 0 0`,
		backgroundColor: "rgba(33,33,33,0.7)",
		WebkitBackdropFilter: "blur(5px)",
		backdropFilter: "blur(5px)",
		color: "#fff",
		padding: theme.spacing(2),
		display: "flex",
		height: "calc(var(--vh, 1vh) * 100 - 45px)",
		[theme.breakpoints.up(480)]: {
			height: "100vh",
			borderRadius: "0",
			width: "80%",
			maxWidth: "442px",
			left: "auto",
			backgroundColor: theme.palette.background.paper,
			padding: 0,
			color: theme.palette.text.primary
		},
		[theme.breakpoints.up(769)]: {
			width: "50%"
		}
	},
	backdrop: {
		backgroundColor: "rgba(0,0,0,0.1)",
		[theme.breakpoints.up(480)]: {
			backgroundColor: "transparent",
			WebkitBackdropFilter: "blur(1.8px)",
			backdropFilter: "blur(1.8px)"
		}
	},
	swipeableContainer: {
		height: "calc(100vh - 50px)",
		[theme.breakpoints.up(480)]: {
			height: "auto"
		}
	}
}));

type Props = Omit<RouteDrawerProps, "swipeable" | "matchRoute"> & {};

const CheckoutDrawer = ({ className, ...otherProps }: Props) => {
	const { locale } = useLocale();
	const location = useLocation();
	const navigate = useNavigate();
	const [currentPanelIndex, setCurrentPanelIndex] = useState(1);
	const [panelCount, setPanelCount] = useState(2);
	const classes = useStyles();
	const { fullName } = useCart();
	const mobile = useMediaQuery("(max-width:480px)");

	const { hash } = location;

	useEffect(() => {
		if (hash.startsWith("#checkout")) {
			if (hash.startsWith("#checkout:")) {
				const index = hash.indexOf(":") > -1 ? parseInt(hash.split(":")[1]) : 1;
				if (index > 1 && fullName == null) {
					navigate("#checkout:1");
				} else {
					setCurrentPanelIndex(index);
				}
			} else {
				navigate("#checkout:1");
			}
		}
	}, [hash, fullName, navigate, setCurrentPanelIndex]);

	useEffect(() => {
		if (fullName != null) {
			setPanelCount(3);
		}
	}, [fullName, setPanelCount]);

	const onClose = useMemo(
		() => () => {
			if (hash.startsWith("#checkout")) {
				navigate("#");
			}
		},
		[hash, navigate]
	);

	const handleChange = useMemo(
		() => (value: string) => {
			navigate(`/${locale}/${value}`);
		},
		[locale, navigate]
	);

	const onCustomerInfoChanged = () => {
		setPanelCount(3);
		// setCurrentPanelIndex(2);
		// window.history.pushState(null, "", "#checkout:2");
		navigate("#checkout:2");
	};

	const onCheckoutClicked = () => {
		console.log("Submit the order");
	};

	const props: RouteDrawerProps = Object.assign(
		{
			className: clsx(classes.root, className),
			classes: { paper: classes.paper },
			BackdropProps: {
				className: classes.backdrop
			},
			anchor: mobile ? "bottom" : "right",
			disableSwipeToOpen: true,
			swipeable: false,
			matchRoute: ({ location: { hash } }) => hash.startsWith("#checkout"),
			onClose,
			onOpen: () => {}
		},
		otherProps
	);

	const panelRenderer = params => {
		const { index } = params;
		switch (mod(index, 3)) {
			case 0:
				return <CartModal onChange={handleChange} key={`panel_${index}`} />;

			case 1:
				return <CustomerInfoPanel onChange={onCustomerInfoChanged} key={`panel_${index}`} />;

			case 2:
				return <CheckoutPanel onChange={onCheckoutClicked} key={`panel_${index}`} />;

			default:
				return null;
		}
	};

	return (
		<RouteDrawer {...props}>
			<>
				<IconButton className={classes.closeButton} onClick={onClose}>
					<Close className={classes.closeIcon} />
				</IconButton>
				<MobileContainerDrawer>
					<VirtualizeSwipeableViews
						slideCount={panelCount}
						slideRenderer={panelRenderer}
						className={classes.swipeableContainer}
						ignoreNativeScroll
						index={currentPanelIndex}
						// onChangeIndex={onChangeIndex}
					/>
				</MobileContainerDrawer>
				<DesktopContainerDrawer>
					<DesktopCheckoutPanel key={"panel_checkout"} onChange={onCheckoutClicked} />
				</DesktopContainerDrawer>
			</>
		</RouteDrawer>
	);
};

export default CheckoutDrawer;
